<template>
    <div class="field" :class="[
        props.icon ? 'with-icon' : '',
        props.isError || props.error ? 'with-error' : ''
    ]">
        <div>
            <input
                v-bind:class="{
                    'without-placeholder': !props.placeholder,
                    clearable: props.clearable && !props.readonly,
                    info: !!props.info
                }"
                ref="field"
                :type="data.type === 'time' || props.mask ? 'text' : data.type"
                :name="props.name"
                :min="props.min"
                :max="props.max"
                :maxlength="props.maxlength"
                :disabled="props.disabled"
                :placeholder="props.placeholder"
                :autocomplete="autocomplete"
                :readonly="props.readonly"
                @input="onInput"
                @blur="onBlur"
                @keyup.enter="onPressEnter"
            />

            <span class="placeholder" :title="props.placeholder">{{ props.placeholder }}</span>

            <Icon v-if="props.icon" :icon="props.icon" :sprite="props.sprite" />

            <div class="actions-block">
                <IconButton
                    v-if="props.type === 'password' && data.type === 'password'"
                    tabindex="-1"
                    icon="eye-closed" sprite="actions"
                    class="tertiary size-s"
                    @click="showPassword"
                />
    
                <IconButton
                    v-else-if="props.type === 'password' && data.type === 'text'"
                    tabindex="-1"
                    icon="eye-opened" sprite="actions"
                    class="tertiary size-s"
                    @click="hidePassword"
                />
    
                <IconButton
                    v-else-if="props.clearable && !props.readonly"
                    tabindex="-1"
                    icon="close" sprite="navigation"
                    class="tertiary size-s clean"
                    @click="clean"
                />

                <Icon v-if="props.info" icon="help" sprite="sections" class="icon-info" :title="props.info" />
            </div>
        </div>
        <span class="error">{{ props.error }}</span>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconButton from '🔗/components/ui/IconButton.vue';
import IMask from 'imask';

const props = defineProps({
    isError: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    sprite: { type: String, default: '' },
    value: { type: [String, Number, Object], default: '' },
    name: { type: [String, Number], default: '' },
    id: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    type: { type: String, default: 'text' },
    maxlength: { type: [Number, null], default: null },
    max: { type: Number, default: null },
    min: { type: Number, default: null },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    autocomplete: { type: String, default: 'off' },
    readonly: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    info: { type: [String, Boolean], default: false },
    mask: { type: [Object, null], default: null },
    autofocus: { type: [String, Boolean], default: false }
});

const { t } = useI18n();

const field = ref(null);

const data = reactive({
    type: props.type
});

const emit = defineEmits(['change', 'pressEnter']);

defineExpose({ field, data });

const showPassword = () => {
    data.type = 'text';
};

const hidePassword = () => {
    data.type = 'password';
};

const clean = () => {
    field.value.value = '';
    setTimeout(() => {
        onInput({ target: field.value });
        field.value.focus();
    });
};

onMounted(() => {
    if (field.value && props.mask) {
        data.maskInstance = IMask(field.value, props.mask);

        data.maskInstance.on('accept', () => {
            emit('change', { target: data.maskInstance });
        });

        if (props.value !== null) {
            if (props.mask.format) {
                data.maskInstance.value = props.mask.format(props.value);
            }
            data.maskInstance.typedValue = props.value;
        }

    } else {
        if (props.value !== null) {
            field.value.value = props.value;
        }
    }

    if (props.autofocus) {
        field.value.focus();
    }
});

const onPressEnter = (evn) => {
    onInput(evn);
    emit('pressEnter', evn);
};

const onBlur = (evn) => {
    let value = evn.target.value.trimStart();

    if (value) {
        if (typeof props.min === 'number') {
            if (parseFloat(value, 10) < props.min || Number.isNaN(parseInt(value, 10))) {
                value = props.min;

                setTimeout(() => {
                    if (evn.target && evn.target.value !== value) {
                        evn.target.value = value;
                    }

                    emit('change', evn);
                });
            }
        }
    }
}

const onInput = (evn) => {
    if (!evn?.target || props.mask) {
        return;
    }

    if (['.', ',', '-'].includes(evn.data)) {
        if (!evn.target.value) {
            emit('change', evn);
        }
        return;
    }

    let value = evn.target.value.trimStart();

    if (value) {
        if (typeof props.min === 'number') {
            // if (parseFloat(value, 10) < props.min || Number.isNaN(parseInt(value, 10))) {
            //     value = props.min;
            // }
        }

        if (typeof props.max === 'number') {
            if (parseFloat(value, 10) > props.max) {
                value = props.max;
            }
        }
    } else {
        value = '';
    }


    evn.target.value = value;

    setTimeout(() => {
        if (evn.target && evn.target.value !== value) {
            evn.target.value = value;
        }
    });

    emit('change', evn);
};
</script>

<style lang="scss" scoped>
div.field {
    display: flex;
    flex-direction: column;

    & > div {
        margin: 0 0 8px;
        display: flex;
        position: relative;
        width: 100%;
        max-width: var(--input-max-width);

        .placeholder {
            @include input-placeholder;
        }

        input {
            @include input;
            &.clearable {
                padding-right: 40px;
                &.info {
                    padding-right: 80px;
                }
            }

            &.info {
                padding-right: 40px;
                &.clearable {
                    padding-right: 80px;
                }
            }
        }

        .actions-block {
            position: absolute;
            display: flex;
            align-items: center;
            right: 8px;
            height: 100%;
            svg {
                margin: 0 8px;
            }
        }
 
        svg {
            &.icon-info {
                fill: var(--primary-80);
                stroke: var(--tertiary-500);
                width: 20px;
                height: 20px;
                cursor: pointer;

                &:hover {
                    fill: var(--primary-500);
                    stroke: var(--tertiary-500);
                }
            }
            &:not(.icon-info) {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                width: 20px;
                height: 20px;
                fill: var(--dark-500);
                stroke: var(--dark-80);
            }
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-left: 52px;
            }

            .placeholder {
                left: 53px;
            }
        }
    }

    & > .error {
        font: var(--font-label);
        color: var(--secondary-500) !important;
        display: flex;
        min-height: 11px;
    }

    &.with-error {
        & > div {
            input {
                border-color: var(--secondary-500) !important;
            }

            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                color: var(--secondary-500) !important;
            }

            input:not(:placeholder-shown) + .placeholder + svg,
            input:focus + .placeholder + svg {
                fill: var(--secondary-500) !important;
                stroke: var(--secondary-80) !important;
            }
        }
    }

    &.light {
        & > div {
            input {
                padding-top: 8px;
                padding-bottom: 8px;
                height: 38px;
            }

            svg {
                height: 18px;
                wigth: 18px;
            }

            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: none;
            }
        }
    }

    &.with-placeholder {
        & > div {
            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: flex;

                top: -6px;
                background: var(--white-500);
            }
        }
    }

    &.small {
        & > div {
            input {
                padding-top: 0;
                padding-bottom: 0;
                height: 24px;
            }

            svg {
                height: 24px;
            }

            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: none;
            }
        }
    }

    &.noerror {
        & > div {
            margin: 0;
        }
        & > .error {
            display: none;
        }
    }

    &.inside {
        input {
            border: none;
            background-color: var(--light-20);
        }
    }
}

div.field.rtl,
.direction-rtl div.field:not(.ltr) {
    direction: rtl;
    & > div {
        .placeholder {
            left: auto;
            right: 20px;
        }

        input {
            &.clearable {
                padding-right: 20px;
                padding-left: 40px;
                &.info {
                    padding-right: 20px;
                    padding-left: 80px;
                }
            }

            &.info {
                padding-right: 20px;
                padding-left: 40px;
                &.clearable {
                    padding-right: 20px;
                    padding-left: 80px;
                }
            }
        }

        .actions-block {
            right: auto;
            left: 8px;
        }
 
        svg {
            &:not(.icon-info) {
                left: auto;
                right: 16px;
            }
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-right: 52px;
                &:not(.clearable):not(.info) {
                    padding-left: 20px;
                }
            }

            .placeholder {
                left: auto;
                right: 53px;
            }
        }
    }
}
</style>
